import { Box } from "@mantine/core";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";

import { LooseObject } from "~/utils/Types";

import ImageTile, { TILE_CAPTION_HEIGHT, TILE_IMAGE_ASPECT } from "./ImageTile";

const GRID_GAP = 12;
const MIN_COL_WIDTH = 162;

/**
 * Virtualised grid of uniformly sized image tiles.
 *
 * Tile height is derived from the measured column width rather than being a
 * constant, so the image area holds TILE_IMAGE_ASPECT at every window size.
 * Every tile is identical, so `estimateSize` stays exact and no per-element
 * measurement is needed.
 */
const ImageTileGrid = ({
  rows,
  bodyIdKey,
  dateKey,
  idKey,
  imgUrlKey,
  keyframeHashKey,
  locationKey,
  scoreKey,
  scoreLabel,
  onItemClick,
  renderItemOverlay,
}: {
  rows: LooseObject[];
  bodyIdKey: string;
  dateKey: string;
  idKey: string;
  imgUrlKey: string;
  keyframeHashKey: string;
  locationKey: string;
  scoreKey?: string;
  scoreLabel?: string;
  onItemClick: (item: LooseObject) => void;
  renderItemOverlay?: (item: LooseObject) => ReactNode;
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const columnObserverRef = useRef<ResizeObserver | null>(null);
  const [columnWidth, setColumnWidth] = useState(MIN_COL_WIDTH);
  const [columnCount, setColumnCount] = useState(1);

  // Callback ref so the first measure happens as the scroll container mounts:
  // the grid can lag the initial render while the camera settings query
  // resolves, which an effect keyed on mount would miss.
  const setScrollNode = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    columnObserverRef.current?.disconnect();
    if (!node) return;
    const update = () => {
      const width = node.clientWidth;
      const count = Math.max(
        1,
        Math.floor((width + GRID_GAP) / (MIN_COL_WIDTH + GRID_GAP)),
      );
      setColumnCount(count);
      setColumnWidth((width - (count - 1) * GRID_GAP) / count);
    };
    update();
    columnObserverRef.current = new ResizeObserver(update);
    columnObserverRef.current.observe(node);
  }, []);

  const tileHeight = columnWidth / TILE_IMAGE_ASPECT + TILE_CAPTION_HEIGHT;
  const rowCount = Math.ceil(rows.length / columnCount);
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => tileHeight,
    gap: GRID_GAP,
    overscan: 2,
  });

  // `estimateSize` is not part of the measurement memo key — only a `measure()`
  // invalidates cached row sizes. Without this, a resize that changes tile
  // height without changing the column count leaves a stale scroll height.
  const { measure } = rowVirtualizer;
  useEffect(() => measure(), [measure, tileHeight]);

  return (
    <div
      ref={setScrollNode}
      tabIndex={0}
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        // Tile height is derived from clientWidth, which a scrollbar changes —
        // reserving the gutter stops height and scrollbar oscillating when the
        // content is a hair taller than the viewport.
        scrollbarGutter: "stable",
      }}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          height: rowVirtualizer.getTotalSize(),
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const startIndex = virtualRow.index * columnCount;
          return (
            <div
              key={virtualRow.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: tileHeight,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <Box
                component="ul"
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
                  // Pins each tile to the height the row was sized for. Without
                  // a definite track height the tile's `height: 100%` resolves
                  // to auto and the image renders at its own aspect ratio.
                  gridAutoRows: tileHeight,
                  gap: GRID_GAP,
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                }}
              >
                {rows
                  .slice(startIndex, startIndex + columnCount)
                  .map((item) => (
                    <ImageTile
                      key={`image_tile_${item[idKey]}_${item[keyframeHashKey]}`}
                      item={item}
                      bodyIdKey={bodyIdKey}
                      dateKey={dateKey}
                      idKey={idKey}
                      imgUrlKey={imgUrlKey}
                      keyframeHashKey={keyframeHashKey}
                      locationKey={locationKey}
                      scoreKey={scoreKey}
                      scoreLabel={scoreLabel}
                      onClick={onItemClick}
                      renderOverlay={renderItemOverlay}
                    />
                  ))}
              </Box>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImageTileGrid;
